import { useEffect, useRef, useState } from 'react';
import { useRootStore } from '../hooks/useRootStore';
import { Employee } from '../data/Employee';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { MDXEditor, MDXEditorMethods } from '@mdxeditor/editor';
import { MdxPlugins } from './MarkdownEditor/MarkdownEditorPlugins';
import { IssueState } from '../data/IssueState';
import MarkdownView from './MarkdownView';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { AgentType } from '../data/AgentType';
import { Box, Button, Dialog, CircularProgress, Link, DialogActions, DialogContent, DialogTitle } from '@mui/material';

export default function ChangeErrorCausersDialog({ issueId, issueState, errorCausersComment, onSubmit, onCancel }: { issueId: number, issueState: IssueState, errorCausersComment: string, onSubmit: (causers: Employee[]) => void, onCancel: () => void }) {

    const [causers, setCausers] = useState<Employee[]>([]);
    const [employee, setEmployee] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [commentsNotSpecified, setCommentsNotSpecified] = useState(false);
    
    const { api } = useRootStore();

    useEffect(() => {
        setLoading(true);
        let loaded = 0;
        api.getIssueErrorCausers(issueId).then(causers => {
            setCausers(causers);
            if (++loaded == 2) {
                setLoading(false);
            }
        });
        api.getEmployees(AgentType.Employee).then(employees => {
            setEmployee(employees as Employee[])
            if (++loaded == 2) {
                setLoading(false);
            }
        });
    }, [issueId, api]);

    const mdxEditorRef = useRef<MDXEditorMethods>(null);

    const editable = [
        IssueState.accepted,
        IssueState.commentsWaiting,
        IssueState.criticalWaiting,
        IssueState.testing].includes(issueState);

    const submit = async () => {
        const comments = (mdxEditorRef.current?.getMarkdown() ?? '').trim();
        if (comments.trim().length == 0 && causers.length > 0) {
            setCommentsNotSpecified(true);
            return false;
        } else {
            await api.updateIssueErrorCausers(issueId, causers, comments)
                .then(() => onSubmit(causers));
        }
    }

    const columns: GridColDef[] = [
        {
            field: 'ID', headerName: 'Сотрудник', width: 300, renderCell: (params) => <Link href={`/employees/${params.value}`}>{params.row.Name}</Link>
        },
    ];

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={true}
            onClose={onCancel}
            sx={{ maxHeight:'95vh' }}
        >
            <DialogTitle>{editable ? 'Изменение' : 'Просмотр'} виновных в ошибке</DialogTitle>
            <HighlightOffOutlinedIcon sx={{ position: 'absolute', right: 10, top: 10, cursor: 'pointer' }} onClick={onCancel} />
            <DialogContent sx={{ minHeight: '60vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
                <Box sx={{ pl: 0, pr: 3, pt: 1, pb: 1, flex: 0, }}>
                    Виновные сотрудники: {causers.length == 0
                        ? "нет виновных" :
                        <span>
                            {causers.map((w, i) => <span>{i == 0 ? '' : ', '}<Link key={w.ID} href={`/employees/${w.ID}`}>{w.Name}</Link></span>)}
                        </span>
                    }
                </Box>
                <Box sx={{ mt: 0.5, flex: 1, textAlign: 'left', minHeight: '220px', border: editable ? `1px solid ${commentsNotSpecified ? 'red' : '#cccccc'}` : 'none', borderRadius: 1 }} onClick={() => mdxEditorRef.current?.focus()}>
                    {editable ?
                        <MDXEditor
                            ref={mdxEditorRef}
                            markdown={errorCausersComment}
                            plugins={MdxPlugins.standardPlugins(api, issueId)}
                            onChange={() => setCommentsNotSpecified(false)}
                        />
                        : <MarkdownView markup={errorCausersComment} />
                    }
                </Box>                
                {loading
                    ? <Box sx={{ textAlign: 'center', p: 5 }}><CircularProgress /></Box> :
                        !editable ? '' :
                            <DataGrid
                                onRowSelectionModelChange={(ids) => {
                                    setCausers(employee.filter(x => ids.includes(x.ID)));
                                }}
                                density='compact'
                                hideFooter={employee.length <= 100}
                                rows={employee}
                                rowSelectionModel={causers.map(x => x.ID)}
                                getRowId={(row) => row.ID}
                                columns={columns}
                                checkboxSelection
                                sx={{ border: 0, m: 0, mt:3 }}
                            />
                }
            </DialogContent>
            {!editable ? '' :
                <DialogActions sx={{ pr: 3 }}>
                    <Button onClick={onCancel}>Отмена</Button>
                    <Button onClick={submit}>Сохранить</Button>
                </DialogActions>
            }
        </Dialog>
    );
}